import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { downloadBlob, formatApiError, setActiveRegistration } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  ShieldCheck, FileText, DownloadSimple, Lock, CaretLeft, ArrowLeft,
  CheckCircle, Circle, SealCheck, Warning, ArrowsClockwise, Ticket,
} from "@phosphor-icons/react";

const todayISO = () => new Date().toISOString().slice(0, 10);
const CADENCE_STYLE = {
  Monthly: "bg-sky-50 text-sky-700",
  Quarterly: "bg-violet-50 text-violet-700",
  Annual: "bg-indigo-50 text-indigo-700",
};

export default function SOC2Hub() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [policies, setPolicies] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [active, setActive] = useState(null);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [exporting, setExporting] = useState("");
  const [ackDate, setAckDate] = useState(todayISO());
  const [ackBusy, setAckBusy] = useState(false);
  const [savingItem, setSavingItem] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerInput, setOwnerInput] = useState("");
  const [archPreparedFor, setArchPreparedFor] = useState("");
  const [ownerBusy, setOwnerBusy] = useState(false);

  const isSuper = !!user?.is_superadmin;

  const loadList = useCallback(() => {
    api.get("/console/soc2/policies")
      .then((r) => { setPolicies(r.data.policies || []); setReadiness(r.data.readiness || null); })
      .catch((e) => { setPolicies([]); toast.error(formatApiError(e.response?.data?.detail)); });
  }, []);

  useEffect(() => {
    if (!isSuper) return;
    loadList();
    api.get("/console/soc2/settings")
      .then((r) => { setOwnerEmail(r.data.owner_email || ""); setOwnerInput(r.data.owner_email || ""); })
      .catch(() => {});
  }, [isSuper, loadList]);

  const saveOwner = async () => {
    setOwnerBusy(true);
    try {
      const r = await api.put("/console/soc2/settings", { owner_email: ownerInput.trim() });
      setOwnerEmail(r.data.owner_email || ""); setOwnerInput(r.data.owner_email || "");
      toast.success("Recurring-task owner saved.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setOwnerBusy(false); }
  };

  const openInternalFlow = () => {
    setActiveRegistration("claro-internal");
    window.location.assign("/claroflow");
  };

  const openDoc = useCallback((id) => {
    setLoadingDoc(true);
    api.get(`/console/soc2/policies/${id}`)
      .then((r) => { setActive(r.data); setAckDate(r.data.acknowledged_at || todayISO()); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoadingDoc(false));
  }, []);

  const exportPdf = async (id) => {
    setExporting(id);
    try { await downloadBlob(`/console/soc2/policies/${id}/export`, `SOC2_${id}.pdf`); }
    catch (e) { toast.error(e.message || "Export failed."); }
    finally { setExporting(""); }
  };

  const acknowledge = async () => {
    setAckBusy(true);
    try {
      const r = await api.post(`/console/soc2/policies/${active.id}/acknowledge`, { effective_date: ackDate });
      setActive(r.data); loadList();
      toast.success("Policy marked reviewed & effective.");
      if (r.data.next_review_scheduled)
        toast.info(`Next annual review ticket scheduled in ClaroFlow for ${r.data.next_review_scheduled}.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAckBusy(false); }
  };

  const revokeAck = async () => {
    setAckBusy(true);
    try {
      const r = await api.post(`/console/soc2/policies/${active.id}/unacknowledge`, {});
      setActive(r.data); loadList();
      toast.success("Acknowledgement revoked.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAckBusy(false); }
  };

  const toggleEvidence = async (item) => {
    setSavingItem(item.key);
    try {
      const r = await api.post(`/console/soc2/policies/${active.id}/evidence/${item.key}`, {
        collected: !item.collected, note: item.note || "",
      });
      setActive(r.data); loadList();
      if (r.data.next_task_scheduled)
        toast.info(`Next ${item.cadence || "recurring"} ClaroFlow ticket scheduled for ${r.data.next_task_scheduled}.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingItem(""); }
  };

  // ---- Access control: Super Admin only (server also enforces 403) ----
  if (!isSuper) {
    return (
      <div data-testid="soc2-forbidden" className="max-w-xl mx-auto mt-24 text-center">
        <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-red-600 mb-4">
          <Lock size={26} weight="fill" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900">403 — Forbidden</h1>
        <p className="mt-2 text-slate-600">The SOC 2 Readiness Hub is restricted to Super Admins.</p>
        <Button data-testid="soc2-forbidden-back" variant="outline" className="mt-6" onClick={() => nav("/")}>
          <ArrowLeft size={16} className="mr-1.5" /> Back to app
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="soc2-hub" className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-3">
        <Button variant="ghost" size="sm" data-testid="soc2-back-console" onClick={() => nav("/console")} className="text-slate-500 -ml-2">
          <CaretLeft size={16} className="mr-1" /> Back to ClaroConsole
        </Button>
      </div>

      <div className="flex items-start gap-3 mb-8">
        <div className="h-11 w-11 shrink-0 rounded-xl bg-[#071B3E] text-white flex items-center justify-center">
          <ShieldCheck size={24} weight="fill" />
        </div>
        <div className="flex-1">
          <h1 data-testid="soc2-title" className="text-2xl font-bold text-slate-900 tracking-tight">SOC 2 Readiness Hub</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Foundational, read-only compliance policies for the single-contributor operating model —
            with per-document review sign-off and an audit-evidence checklist. Restricted to Super Admins.
          </p>
        </div>
      </div>

      {!active ? (
        <>
          {readiness && (
            <div data-testid="soc2-readiness" className="mb-6 rounded-xl border border-slate-200 bg-white p-5">
              <div className="flex items-center gap-5">
                <div className="relative h-20 w-20 shrink-0" data-testid="soc2-readiness-score"
                     style={{ background: `conic-gradient(#128C77 ${readiness.score * 3.6}deg, #E6EAF0 0deg)`, borderRadius: "9999px" }}>
                  <div className="absolute inset-[6px] rounded-full bg-white flex items-center justify-center">
                    <span className="text-xl font-bold text-[#071B3E]">{readiness.score}%</span>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900">Overall SOC 2 Readiness</h3>
                  <p className="text-[13px] text-slate-500 mt-0.5">
                    Combines policy acknowledgements and audit-evidence collected across all documents.
                  </p>
                  <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-[13px]">
                    <span className="text-slate-600">Policies reviewed: <b className="text-slate-900">{readiness.policies_acknowledged}/{readiness.policies_total}</b></span>
                    <span className="text-slate-600">Evidence collected: <b className="text-slate-900">{readiness.evidence_collected}/{readiness.evidence_total}</b></span>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div data-testid="soc2-recurring-panel" className="mb-6 rounded-xl border border-slate-200 bg-white p-5">
            <div className="flex items-start gap-3 flex-wrap">
              <div className="h-10 w-10 shrink-0 rounded-lg bg-violet-50 text-violet-700 flex items-center justify-center">
                <ArrowsClockwise size={20} weight="bold" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-slate-900">Recurring Tasks &amp; ClaroFlow</h3>
                <p className="text-[13px] text-slate-500 mt-0.5 leading-snug">
                  When you complete a recurring item (monthly / quarterly / annual), a ticket for the next
                  cycle is auto-created in the private ClaroCompliance ClaroFlow workspace and assigned to the owner below.
                </p>
                <div className="mt-3 flex items-end gap-2 flex-wrap">
                  <div>
                    <label className="text-[11px] uppercase tracking-wide text-slate-400">Recurring-task owner (email)</label>
                    <Input data-testid="soc2-owner-input" value={ownerInput} placeholder="compliance@clarocompliance.com"
                           onChange={(e) => setOwnerInput(e.target.value)} className="mt-1 h-9 w-72" />
                  </div>
                  <Button size="sm" data-testid="soc2-owner-save" disabled={ownerBusy || ownerInput.trim() === ownerEmail}
                          onClick={saveOwner} className="h-9 bg-[#071B3E] hover:bg-[#0E2A5E]">
                    {ownerBusy ? "Saving…" : "Save owner"}
                  </Button>
                  <Button size="sm" variant="outline" data-testid="soc2-open-flow" onClick={openInternalFlow} className="h-9">
                    <Ticket size={15} className="mr-1.5" /> Open internal ClaroFlow
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <div data-testid="soc2-architecture-panel" className="mb-6 rounded-xl border border-[#071B3E] bg-[#071B3E] text-white p-5">
            <div className="flex items-start gap-3 flex-wrap">
              <div className="h-10 w-10 shrink-0 rounded-lg bg-white/10 text-white flex items-center justify-center">
                <ShieldCheck size={20} weight="fill" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold">Data Segregation &amp; Encryption Architecture</h3>
                <p className="text-[13px] text-white/70 mt-0.5 leading-snug">
                  An auditor- and customer-ready PDF with flow diagrams showing how each tenant's data is
                  segregated, encrypted in transit and at rest, kept private per customer, and how Super Admin
                  publishes the app and shared NERC Standards — mapped to SOC 2 Trust Services Criteria.
                </p>
                <div className="mt-3 flex items-end gap-2 flex-wrap">
                  <div>
                    <label className="text-[11px] uppercase tracking-wide text-white/50">Prepared for (optional)</label>
                    <Input data-testid="soc2-architecture-preparedfor" value={archPreparedFor}
                           onChange={(e) => setArchPreparedFor(e.target.value)}
                           placeholder="Auditor or customer name"
                           className="mt-1 h-9 w-64 bg-white text-[#071B3E]" />
                  </div>
                  <Button size="sm" data-testid="soc2-architecture-download"
                          onClick={async () => {
                            try {
                              const qs = archPreparedFor.trim() ? `?prepared_for=${encodeURIComponent(archPreparedFor.trim())}` : "";
                              await downloadBlob(`/console/soc2/architecture.pdf${qs}`, "ClaroShield_Data_Segregation_Encryption_Architecture.pdf");
                            } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Download failed"); }
                          }}
                          className="h-9 bg-white text-[#071B3E] hover:bg-slate-100">
                    <DownloadSimple size={15} className="mr-1.5" /> Download architecture PDF
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div data-testid="soc2-policy-list" className="grid gap-4 sm:grid-cols-2">
          {policies === null && <div className="text-slate-400 text-sm">Loading policies…</div>}
          {policies && policies.length === 0 && <div className="text-slate-400 text-sm">No policies available.</div>}
          {(policies || []).map((p) => (
            <div key={p.id} data-testid={`soc2-card-${p.id}`}
                 className="rounded-xl border border-slate-200 bg-white p-5 flex flex-col h-full hover:border-[#071B3E]/40 hover:shadow-sm transition-all">
              <div className="flex items-start gap-3 flex-1">
                <FileText size={20} className="text-[#071B3E] mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900 leading-snug">{p.title}</h3>
                  <div className="mt-1.5">
                    {p.acknowledged ? (
                      <span data-testid={`soc2-badge-${p.id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-semibold px-2 py-0.5">
                        <SealCheck size={12} weight="fill" /> Effective
                      </span>
                    ) : (
                      <span data-testid={`soc2-badge-${p.id}`} className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 text-[11px] font-semibold px-2 py-0.5">
                        <Warning size={12} weight="fill" /> Not reviewed
                      </span>
                    )}
                  </div>
                  <p className="text-[13px] text-slate-500 mt-2 leading-snug">{p.summary}</p>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-3 text-[11px] text-slate-400">
                <span data-testid={`soc2-updated-${p.id}`} className="uppercase tracking-wide">Updated {p.last_updated}</span>
                <span className="uppercase tracking-wide" data-testid={`soc2-evidence-progress-${p.id}`}>
                  Evidence {p.evidence_collected}/{p.evidence_total}
                </span>
              </div>
              <div className="mt-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] uppercase tracking-wide text-slate-400">Area complete</span>
                  <span data-testid={`soc2-percent-${p.id}`} className="text-[13px] font-bold text-[#071B3E]">{p.percent_complete ?? 0}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-[#128C77] transition-all" style={{ width: `${p.percent_complete ?? 0}%` }} />
                </div>
              </div>
              <div className="mt-4 flex items-center justify-end gap-2">
                <Button size="sm" variant="outline" data-testid={`soc2-view-${p.id}`} onClick={() => openDoc(p.id)}>View</Button>
                <Button size="sm" data-testid={`soc2-export-${p.id}`} disabled={exporting === p.id}
                        onClick={() => exportPdf(p.id)} className="bg-[#071B3E] hover:bg-[#0E2A5E]">
                  <DownloadSimple size={15} className="mr-1" />{exporting === p.id ? "…" : "PDF"}
                </Button>
              </div>
            </div>
          ))}
        </div>
        </>
      ) : (
        <div data-testid="soc2-reader">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <Button variant="ghost" size="sm" data-testid="soc2-back" onClick={() => setActive(null)}>
              <CaretLeft size={16} className="mr-1" /> All policies
            </Button>
            <Button size="sm" data-testid={`soc2-export-${active.id}`} disabled={exporting === active.id}
                    onClick={() => exportPdf(active.id)} className="bg-[#071B3E] hover:bg-[#0E2A5E]">
              <DownloadSimple size={15} className="mr-1.5" />{exporting === active.id ? "Exporting…" : "Export to PDF"}
            </Button>
          </div>

          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
            {/* Document */}
            <article className="rounded-xl border border-slate-200 bg-white p-8 order-2 lg:order-1">
              <h2 className="text-xl font-bold text-slate-900">{active.title}</h2>
              <div className="text-[11px] uppercase tracking-wide text-slate-400 mt-1 mb-1">
                {active.org_name} · Last updated {active.last_updated}
              </div>
              {active.summary && <p className="text-sm italic text-slate-500 border-l-2 border-slate-200 pl-3 my-4">{active.summary}</p>}
              <div className="space-y-5 mt-4">
                {(active.sections || []).map((sec, i) => (
                  <section key={i}>
                    <h3 className="font-semibold text-[#071B3E] text-[15px] mb-1.5">{sec.heading}</h3>
                    {(sec.paras || []).map((para, j) => (
                      <p key={j} className="text-[14px] text-slate-700 leading-relaxed mb-2">{para}</p>
                    ))}
                    {(sec.bullets || []).length > 0 && (
                      <ul className="list-disc pl-5 space-y-1">
                        {sec.bullets.map((b, k) => <li key={k} className="text-[14px] text-slate-700 leading-relaxed">{b}</li>)}
                      </ul>
                    )}
                  </section>
                ))}
              </div>
            </article>

            {/* Sidebar: acknowledgement + evidence */}
            <div className="order-1 lg:order-2 space-y-4">
              {/* Acknowledgement */}
              <div data-testid="soc2-ack-panel" className={`rounded-xl border p-4 ${active.acknowledged ? "border-emerald-200 bg-emerald-50/40" : "border-amber-200 bg-amber-50/40"}`}>
                <div className="flex items-center gap-2 mb-2">
                  <SealCheck size={18} weight="fill" className={active.acknowledged ? "text-emerald-600" : "text-amber-500"} />
                  <h4 className="font-semibold text-slate-900 text-sm">Policy Acknowledgement</h4>
                  {active.ack_cadence && (
                    <span data-testid="soc2-ack-cadence" className={`ml-auto inline-flex items-center gap-1 rounded-full text-[10px] font-semibold px-2 py-0.5 ${CADENCE_STYLE.Annual}`}>
                      <ArrowsClockwise size={11} weight="bold" /> Annual
                    </span>
                  )}
                </div>
                {active.acknowledged ? (
                  <>
                    <p data-testid="soc2-ack-status" className="text-[13px] text-slate-700 leading-snug">
                      <b>Reviewed &amp; effective</b> on <b>{active.acknowledged_at}</b>
                      <br />Signed by {active.acknowledged_by}
                      {active.signed_at ? <span className="text-slate-400"> ({new Date(active.signed_at).toISOString().slice(0, 16).replace("T", " ")} UTC)</span> : null}
                    </p>
                    <Button size="sm" variant="outline" data-testid="soc2-unack-btn" disabled={ackBusy}
                            onClick={revokeAck} className="mt-3 w-full border-slate-300">
                      {ackBusy ? "…" : "Revoke acknowledgement"}
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="text-[13px] text-slate-600 leading-snug mb-2">
                      Mark this policy reviewed &amp; effective for your audit file.
                    </p>
                    <label className="text-[11px] uppercase tracking-wide text-slate-400">Effective date</label>
                    <Input type="date" value={ackDate} data-testid="soc2-ack-date" onChange={(e) => setAckDate(e.target.value)} className="mt-1 h-9" />
                    <Button size="sm" data-testid="soc2-ack-btn" disabled={ackBusy || !ackDate}
                            onClick={acknowledge} className="mt-3 w-full bg-emerald-600 hover:bg-emerald-700">
                      {ackBusy ? "Signing…" : "Mark reviewed & effective"}
                    </Button>
                  </>
                )}
              </div>

              {/* Evidence checklist */}
              <div data-testid="soc2-evidence-panel" className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-slate-900 text-sm">Audit Evidence Checklist</h4>
                  <span data-testid="soc2-evidence-count" className="text-[11px] font-semibold text-slate-500">
                    {active.evidence_collected}/{active.evidence_total}
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mb-3">
                  <div className="h-full bg-[#071B3E] transition-all"
                       style={{ width: `${active.evidence_total ? (active.evidence_collected / active.evidence_total) * 100 : 0}%` }} />
                </div>
                <ul className="space-y-2">
                  {(active.evidence || []).map((item) => (
                    <li key={item.key} data-testid={`soc2-evidence-${item.key}`}
                        className="flex items-start gap-2 rounded-lg border border-slate-100 p-2 hover:border-slate-200">
                      <button data-testid={`soc2-evidence-toggle-${item.key}`} disabled={savingItem === item.key}
                              onClick={() => toggleEvidence(item)} className="mt-0.5 shrink-0" aria-label="Toggle collected">
                        {item.collected
                          ? <CheckCircle size={20} weight="fill" className="text-emerald-600" />
                          : <Circle size={20} className="text-slate-300" />}
                      </button>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[13px] font-medium text-slate-800 leading-snug">{item.control}</span>
                          {item.cadence && (
                            <span data-testid={`soc2-cadence-${item.key}`}
                                  className={`inline-flex items-center gap-1 rounded-full text-[10px] font-semibold px-1.5 py-0.5 ${CADENCE_STYLE[item.cadence.charAt(0).toUpperCase() + item.cadence.slice(1)] || "bg-slate-100 text-slate-600"}`}>
                              <ArrowsClockwise size={10} weight="bold" />
                              {item.cadence.charAt(0).toUpperCase() + item.cadence.slice(1)}
                            </span>
                          )}
                        </div>
                        <div className="text-[12px] text-slate-500 leading-snug">{item.evidence}</div>
                        {Array.isArray(item.steps) && item.steps.length > 0 && (
                          <details data-testid={`soc2-evidence-steps-${item.key}`} className="mt-1.5">
                            <summary className="text-[11px] font-semibold text-[#071B3E] cursor-pointer select-none hover:underline">
                              How to capture this evidence
                            </summary>
                            <ol className="mt-1 ml-3 list-decimal space-y-1">
                              {item.steps.map((s, si) => (
                                <li key={si} className="text-[11.5px] text-slate-600 leading-snug">{s}</li>
                              ))}
                            </ol>
                          </details>
                        )}
                        {item.collected && item.updated_by && (
                          <div className="text-[10px] uppercase tracking-wide text-emerald-600 mt-0.5">
                            Collected · {item.updated_by}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          {loadingDoc && <div className="text-slate-400 text-sm mt-3">Loading…</div>}
        </div>
      )}
    </div>
  );
}
